import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { container } from "tsyringe";
import { AnalyticsService } from "../../services/analytics";

export interface Base64EncoderState {
  inputText: string;
  outputText: string;
  isEncoding: boolean;
  isDecoding: boolean;
  operation: 'encode' | 'decode';
}

const initialState: Base64EncoderState = {
  inputText: '',
  outputText: '',
  isEncoding: false,
  isDecoding: false,
  operation: 'encode',
};

export const Base64EncoderSlice = createSlice({
  name: 'base64Encoder',
  initialState,
  reducers: {
    inputTextChanged: (state, action: PayloadAction<string>) => {
      state.inputText = action.payload;
    },
    operationChanged: (state, action: PayloadAction<'encode' | 'decode'>) => {
      state.operation = action.payload;
      state.outputText = '';
    },
    clearOutput: (state) => {
      state.outputText = '';
    },
    clearAll: (state) => {
      state.inputText = '';
      state.outputText = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(encodeTextAsync.pending, (state) => {
        state.isEncoding = true;
      })
      .addCase(encodeTextAsync.fulfilled, (state, action) => {
        state.isEncoding = false;
        state.outputText = action.payload;
      })
      .addCase(encodeTextAsync.rejected, (state) => {
        state.isEncoding = false;
      })
      .addCase(decodeTextAsync.pending, (state) => {
        state.isDecoding = true;
      })
      .addCase(decodeTextAsync.fulfilled, (state, action) => {
        state.isDecoding = false;
        state.outputText = action.payload;
      })
      .addCase(decodeTextAsync.rejected, (state) => {
        state.isDecoding = false;
      });
  },
});

export const encodeTextAsync = createAsyncThunk(
  'base64Encoder/encodeText',
  async (text: string, { getState }) => {
    const analyticsService = container.resolve(AnalyticsService);
    analyticsService.LogEvent({
      category: 'base64_encoder',
      action: 'encode',
      label: `text_length_${text.length}`
    });
    
    try {
      // Encode text to base64
      const encoded = btoa(text);
      return encoded;
    } catch (error) {
      throw new Error('Failed to encode text to base64');
    }
  }
);

export const decodeTextAsync = createAsyncThunk(
  'base64Encoder/decodeText',
  async (text: string, { getState }) => {
    const analyticsService = container.resolve(AnalyticsService);
    analyticsService.LogEvent({
      category: 'base64_encoder',
      action: 'decode',
      label: `text_length_${text.length}`
    });
    
    try {
      // Decode base64 to text
      const decoded = atob(text);
      return decoded;
    } catch (error) {
      throw new Error('Failed to decode base64 text');
    }
  }
);

export const { 
  inputTextChanged, 
  operationChanged, 
  clearOutput, 
  clearAll 
} = Base64EncoderSlice.actions;

export const selectState = (state: RootState) => state.base64Encoder;

export default Base64EncoderSlice.reducer;
